import type { ReactNode } from 'react';
import type {
  DestinationNode,
  OrderDraft,
  OrderService,
  OrderablePackage,
  ShippingChannel,
} from '@mypoz/shared';
import { findDestination } from '@mypoz/shared';
import { useCodeLabel, useTranslation } from '@mypoz/i18n';
import { Card } from '@mypoz/ui';

import { TRANSPORT_TYPE } from '@/features/packages/index.js';
import { actionLinkClass, buttonClass } from '@/lib/styles.js';
import { useSelectionWeight } from '@/features/orders/useSelectionWeight.js';
import { useFormat } from '@/lib/format.js';

export interface ConfirmSummaryProps {
  draft: OrderDraft;
  pool: readonly OrderablePackage[];
  channels: readonly ShippingChannel[];
  services: readonly OrderService[];
  countries: readonly DestinationNode[];
  /** Read-only here: by Confirm, `DeliveryForm` has already triggered the loads that populate these. */
  provincesFor: (countryCode: string) => readonly DestinationNode[];
  citiesFor: (provinceCode: string) => readonly DestinationNode[];
  /** Jumps back to the step that owns a section. */
  onEdit: (stepIndex: number) => void;
  onSubmit: () => void;
  /** A submit is in flight (MYP-91): the button is disabled and says so. */
  submitting?: boolean;
  /** Why the last submit did not create an order, shown above the button. */
  error?: ReactNode;
}

function Section({
  title,
  onEdit,
  editLabel,
  children,
}: {
  title: string;
  onEdit: () => void;
  editLabel: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-base font-semibold text-ink">{title}</h2>
        <button type="button" onClick={onEdit} className={actionLinkClass('shrink-0')}>
          {editLabel}
        </button>
      </div>
      <div className="mt-3 grid gap-1.5 text-sm">{children}</div>
    </Card>
  );
}

/**
 * Step 4 — everything chosen, with a way back into whichever step owns it.
 *
 * There is no total, and that is not an omission. Nothing in this flow is priced: channels carry
 * no rate, and the warehouse sets the amount after it weighs and packs. A summary that invented a
 * figure here would be the most convincing wrong number in the app, so the space that would
 * normally hold a total says instead when the real one arrives.
 */
export function ConfirmSummary({
  draft,
  pool,
  channels,
  services: allServices,
  countries,
  provincesFor,
  citiesFor,
  onEdit,
  onSubmit,
  submitting = false,
  error,
}: ConfirmSummaryProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const weightLabel = useSelectionWeight();
  const codeLabel = useCodeLabel();

  const packages = pool.filter((item) => draft.packageIds.includes(item.id));
  const channel = channels.find((item) => item.id === draft.channelId);
  const services = allServices.filter((item) => draft.serviceIds.includes(item.id));

  const country = findDestination(countries, draft.countryCode);
  const province = findDestination(
    draft.countryCode ? provincesFor(draft.countryCode) : [],
    draft.provinceCode,
  );
  const city = findDestination(
    draft.provinceCode ? citiesFor(draft.provinceCode) : [],
    draft.cityCode,
  );
  const address = [city?.name, province?.name, country?.name].filter(Boolean).join(', ');

  const editLabel = t('orders.newOrder.confirm.edit');

  return (
    <div className="grid gap-5">
      <Section
        title={t('orders.newOrder.confirm.packages')}
        onEdit={() => onEdit(0)}
        editLabel={editLabel}
      >
        {packages.map((item) => (
          <p key={item.id} className="text-ink">
            <span className="font-semibold">{item.trackingNumber}</span>{' '}
            <span className="text-muted">
              {item.itemName} × {fmt.number(item.quantity)}
            </span>
          </p>
        ))}
        <p className="mt-1 text-muted">
          {t('orders.newOrder.summary', {
            selected: fmt.number(packages.length),
            weight: weightLabel(packages),
          })}
        </p>
      </Section>

      <Section
        title={t('orders.newOrder.confirm.channel')}
        onEdit={() => onEdit(1)}
        editLabel={editLabel}
      >
        {channel ? (
          <>
            <p className="font-semibold text-ink">{channel.name}</p>
            <p className="text-muted">
              {codeLabel(TRANSPORT_TYPE, channel.transportType)} ·{' '}
              {t('orders.newOrder.channel.transitDays', {
                min: fmt.number(channel.transitDaysMin),
                max: fmt.number(channel.transitDaysMax),
              })}
            </p>
          </>
        ) : null}
      </Section>

      <Section
        title={t('orders.newOrder.confirm.delivery')}
        onEdit={() => onEdit(2)}
        editLabel={editLabel}
      >
        {draft.recipient.trim() || draft.phone.trim() ? (
          <p className="text-ink">
            {[draft.recipient.trim(), draft.phone.trim()].filter(Boolean).join(' · ')}
          </p>
        ) : null}
        {draft.line1.trim() ? <p className="text-muted">{draft.line1.trim()}</p> : null}
        <p className="text-muted">{[draft.postcode.trim(), address].filter(Boolean).join(' ')}</p>

        <p className="mt-1 text-muted">
          {services.length === 0
            ? t('orders.newOrder.confirm.noServices')
            : services.map((service) => service.name).join(' · ')}
        </p>

        <p className="text-muted">
          {draft.paymentMode === 'MANUAL'
            ? t('orders.newOrder.delivery.paymentManual')
            : t('orders.newOrder.delivery.paymentAuto')}
        </p>

        {draft.couponCode ? (
          <p className="text-muted">
            {t('orders.newOrder.confirm.promo', { code: draft.couponCode })}
          </p>
        ) : null}
        {draft.remark ? <p className="break-words text-muted">{draft.remark}</p> : null}
      </Section>

      <p className="border-l-2 border-brand pl-3 text-sm text-ink">
        {t('orders.newOrder.confirm.noTotal')}
      </p>

      {/*
        The end of the flow, so it looks like it: filled, oversized, and full-width on mobile. It
        was a text link, which put the one irreversible action on the page in the same visual
        register as the Edit links beside it.
      */}
      {error}

      <div className="flex flex-col gap-2 sm:items-end">
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          aria-busy={submitting}
          className={buttonClass(
            'primary',
            'w-full px-10 py-3 text-base disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto',
          )}
        >
          {submitting
            ? t('orders.newOrder.confirm.submitting')
            : t('orders.newOrder.confirm.submit')}
        </button>
        <p className="text-sm text-muted sm:text-right">
          {t('orders.newOrder.confirm.submitNote')}
        </p>
      </div>
    </div>
  );
}
